import { takeLatest, put, select } from 'redux-saga/effects'
import { toast } from '../../components/Toast'
import { resetState } from '../redux-slices'
import { setSelectedLanguagesStart } from '../redux-slices/language'
import { serialize } from 'object-to-formdata'

import {
  getDocuments,
  getDocumentsLabel,
  getUserDetails,
  removeProfileImage,
  updateDocuments,
  updateUserDetails,
  uploadProfileImage,
  getSumSubAccessToken,
  getAllReviews,
  getCashbackBalance,
  getWalletAmount,
  submitOTP,
  resendPhoneOTP,
  changePhoneNumber,
  getAntetrekPopupToken
} from '../../utils/apiCalls'

import {
  getDocumentsFailure,
  getDocumentsLabelFailure,
  getDocumentsLabelStart,
  getDocumentsLabelSuccess,
  getDocumentsStart,
  getDocumentsSuccess,
  getUserDetailsFailure,
  getUserDetailsStart,
  getUserDetailsSuccess,
  removeProfileImageComplete,
  removeProfileImageStart,
  updateDocumentsComplete,
  updateDocumentsStart,
  uploadProfileImageComplete,
  uploadProfileImageStart,
  userUpdateInfoComplete,
  userUpdateInfoStart,
  getSumSubAccessTokenStart,
  getSumSubAccessTokenSuccess,
  getSumSubAccessTokenFailure,
  getAllReviewFailure,
  getAllReviewSuccess,
  getAllReviewStart,
  getCashbackBalanceFailure,
  getCashbackBalanceSuccess,
  getCashbackBalanceStart,
  getWalletAmountStart,
  getWalletAmountSuccess,
  getWalletAmountFailure,
  submitOTPStart,
  submitOTPComplete,
  changePhoneNumberStart,
  changePhoneNumberComplete,
  resendPhoneOTPStart,
  resendPhoneOTPComplete,
  sendStrictPhoneOTPStart,
  sendStrictPhoneOTPComplete,
  setStrictSignupPendingData,
  clearStrictSignupPendingData,
  getAntetrekPopupTokenStart,
  getAntetrekPopupTokenSuccess,
  getAntetrekPopupTokenFailure
} from '../redux-slices/user'
import { USER_UPDATE_TYPES } from 'constants/myAccount'
import { closeModal, openModal } from '../redux-slices/modal'
import { MODALS } from '../../constants/modals'
import React from 'react'
import { userLoginStart } from '../redux-slices/login'
import { PhoneVerification } from '../../components/PhoneVerification'

const getLanguageData = state => state.language.languageData

export default function * userWatcher () {
  yield takeLatest(getUserDetailsStart.type, getUserDetailWorker)
  yield takeLatest(userUpdateInfoStart.type, updateUserInfo)
  yield takeLatest(uploadProfileImageStart.type, uploadProfileImageWorker)
  yield takeLatest(removeProfileImageStart.type, removeProfileImageWorker)
  yield takeLatest(getDocumentsLabelStart.type, getDocumentsLabelWorker)
  yield takeLatest(getDocumentsStart.type, getDocumentsWorker)
  yield takeLatest(updateDocumentsStart.type, updateDocumentsWorker)
  yield takeLatest(getSumSubAccessTokenStart.type, getSumSubAccessTokenWorker)
  yield takeLatest(getAllReviewStart.type, getAllReviewWorker)
  yield takeLatest(getCashbackBalanceStart.type, getCashbackBalanceWorker)
  yield takeLatest(getAntetrekPopupTokenStart.type, getAntetrekPopupTokenWorker)
  yield takeLatest(getWalletAmountStart.type, getWalletAmountWorker)
  yield takeLatest(submitOTPStart.type, submitOTPWorker)
  yield takeLatest(changePhoneNumberStart.type, changePhoneNumberWorker)
  yield takeLatest(resendPhoneOTPStart.type, resendPhoneOTPWorker)
  yield takeLatest(sendStrictPhoneOTPStart.type, sendStrictPhoneOTPWorker)
}

function * getUserDetailWorker () {
  try {
    const { data } = yield getUserDetails()
    const user = data?.data?.getUser
    yield put(getUserDetailsSuccess(user))
    if (window?.cioIdentifyUser && user?.userId && user?.email) {
      window.cioIdentifyUser(user.userId, user.email, user.username, user.createdAt)
    }
  } catch (e) {
    yield put(getUserDetailsFailure(e?.response?.data?.errors[0]?.description))
    if (e?.response?.status === 403 || e?.response?.status === 401) {
      if (window?.cioLogoutUser) {
        window.cioLogoutUser()
      }
      yield put(resetState())
    }
  }
}

function * getAntetrekPopupTokenWorker () {
  try {
    const { data } = yield getAntetrekPopupToken()
    yield put(getAntetrekPopupTokenSuccess(data?.data))
  } catch (e) {
    // Non-critical integration: fail silently, useAntetrekPopup decides whether to retry
    yield put(getAntetrekPopupTokenFailure())
  }
}

function * updateUserInfo (action) {
  const languageData = yield select(getLanguageData)
  try {
    const { initialState, navigate } = action.payload

    yield updateUserDetails(initialState)
    yield put(userUpdateInfoComplete())

    if (initialState?.type === USER_UPDATE_TYPES.SUBSCRIPTION) {
      if ('newsLetter' in initialState) {
        if (initialState.newsLetter === 'true') {
          yield toast(`${languageData?.newsletterOptInSuccess || ''}`, 'success')
        } else {
          yield toast(`${languageData?.newsletterOptOutSuccess || ''}`, 'success')
        }
      }

      if ('sms' in initialState) {
        if (initialState.sms === 'true') {
          yield toast(`${languageData?.smsOptInSuccess || ''}`, 'success')
        } else {
          yield toast(`${languageData?.smsOptOutSuccess || ''}`, 'success')
        }
      }

      if ('newsletterAndSms' in initialState) {
        if (initialState.newsletterAndSms === 'true') {
          yield toast(`${languageData?.newsletterAndSmsOptInSuccess || ''}`, 'success')
        } else {
          yield toast(`${languageData?.newsletterAndSmsOptOutSuccess || ''}`, 'success')
        }
      }
    } else if (initialState?.password) {
      yield put(resetState())
      yield toast(`${languageData?.loginPassword} ${languageData?.updatedSuccess}`, 'success')
    } else {
      yield toast(`${languageData?.account} ${languageData?.updatedSuccess}`, 'success')
    }

    if (initialState?.preferredLanguage) {
      yield put(setSelectedLanguagesStart({
        selectedLanguageCode: initialState?.preferredLanguage,
        navigate
      }))
    }
    if (initialState?.type !== USER_UPDATE_TYPES.PASSWORD) {
      yield put(getUserDetailsStart())
    }
  } catch (e) {
    yield put(userUpdateInfoComplete())
    yield toast(languageData?.samePasswordError || e?.response?.data?.errors[0]?.description || 'Something went wrong', 'error')
  }
}

function * uploadProfileImageWorker (action) {
  try {
    const { image } = action.payload
    const languageData = yield select(getLanguageData)
    yield uploadProfileImage({ data: serialize({ profileImage: image }) })
    yield put(uploadProfileImageComplete())

    yield toast(`${languageData?.profileImage} ${languageData?.updatedSuccess}`, 'success')

    yield put(getUserDetailsStart())
  } catch (e) {
    yield put(uploadProfileImageComplete())
    yield toast(e?.response?.data?.errors[0]?.description, 'error')
  }
}

function * removeProfileImageWorker () {
  try {
    const languageData = yield select(getLanguageData)
    yield removeProfileImage()
    yield put(removeProfileImageComplete())
    yield toast(`${languageData?.profileImageRemoved}`, 'success')

    yield put(getUserDetailsStart())
  } catch (e) {
    yield put(removeProfileImageComplete())
    yield toast(e?.response?.data?.errors[0]?.description, 'error')
  }
}

function * getDocumentsLabelWorker () {
  try {
    const { data } = yield getDocumentsLabel()
    yield put(getDocumentsLabelSuccess(data))
  } catch (e) {
    yield put(getDocumentsLabelFailure())
  }
}

function * getDocumentsWorker () {
  try {
    const { data } = yield getDocuments()

    yield put(getDocumentsSuccess(data.data.userDocument))
  } catch (e) {
    yield put(getDocumentsFailure())

    yield toast(e?.response?.data?.errors[0]?.description, 'error')
  }
}

function * updateDocumentsWorker (action) {
  try {
    const { data } = action && action.payload

    const languageData = yield select(getLanguageData)

    yield updateDocuments({ data: serialize(data) })

    yield put(updateDocumentsComplete())

    yield put(getDocumentsStart())

    yield put(getUserDetailsStart())

    yield toast(`${languageData?.docs} ${languageData?.updatedSuccess}`, 'success')
  } catch (e) {
    yield put(updateDocumentsComplete())

    yield toast(e?.response?.data?.errors[0]?.description, 'error')
  }
}

function * getSumSubAccessTokenWorker (action) {
  try {
    const { data } = yield getSumSubAccessToken()

    yield put(getSumSubAccessTokenSuccess(data?.data?.token))
  } catch (e) {
    yield put(getSumSubAccessTokenFailure())

    yield toast(e?.response?.data?.message, 'error')
  }
}

function * getAllReviewWorker () {
  try {
    const { data } = yield getAllReviews()

    yield put(getAllReviewSuccess(data?.data?.reviews))
  } catch (e) {
    yield put(getAllReviewFailure())

    yield toast(e?.response?.data?.errors[0].description, 'error')
  }
}

function * getCashbackBalanceWorker () {
  const languageData = yield select(getLanguageData)
  try {
    const { data } = yield getCashbackBalance()

    yield put(getCashbackBalanceSuccess(data?.data?.cashback))
  } catch (e) {
    yield put(getCashbackBalanceFailure())

    yield toast(languageData?.PhoneNotVerifiedErrorType || e?.response?.data?.errors[0].description, 'error')
  }
}

function * getWalletAmountWorker () {
  try {
    const { data } = yield getWalletAmount()

    yield put(getWalletAmountSuccess(data?.data?.amount?.amount))
  } catch (e) {
    yield put(getWalletAmountFailure())

    yield toast(e?.response?.data?.errors[0].description, 'error')
  }
}

function * submitOTPWorker (action) {
  const languageData = yield select(getLanguageData)
  try {
    const { data, setPhoneDisable, setShow, onSuccess, signupData, navigate, returnTo, setDisableSubmit } = action.payload

    // For STRICT flow: merge signup fields into the OTP payload so the backend
    // performs signup internally and verifies the phone in a single step.
    const otpPayload = signupData ? { ...signupData, ...data } : data
    yield submitOTP(otpPayload)

    yield put(submitOTPComplete())
    yield put(clearStrictSignupPendingData())

    if (setPhoneDisable) {
      yield setPhoneDisable(true)
    }

    yield toast('OTP ' + languageData?.verificationCompletedSuccessfully, 'success')

    if (signupData) {
      // STRICT flow: OTP+signup succeeded; now auto-login (no separate signup API call).
      // ORDER MATTERS:
      // 1. Clear URL first — so when modal close triggers UrlHandler, tab is already null
      // 2. Close modals — UrlHandler fires but sees tab=null, won't reopen AUTH
      // 3. Toast + dispatch login in background
      if (navigate) {
        const params = new URLSearchParams(window.location.search)
        params.delete('auth-modal')
        navigate(
          { pathname: window.location.pathname, search: params.toString() },
          { replace: true }
        )
      }

      yield put(closeModal(MODALS.PHONE_VERIFICATION_MODAL))
      yield put(closeModal(MODALS.AUTH))

      yield toast(`${languageData?.signupSuccess}`, 'success')
      yield put(userLoginStart({ ...signupData, isSignup: true, navigate, returnTo, setDisableSubmit }))
    } else {
      // MILD / account phone-change flow
      yield toast(`${languageData?.loginSuccess}`, 'success')
      yield put(getUserDetailsStart())

      if (setShow) {
        yield setShow(false)
      }

      if (onSuccess) {
        yield onSuccess()
      }
    }
  } catch (e) {
    yield put(submitOTPComplete())

    const errorCode = e?.response?.data?.errors?.[0]?.errorCode
    let errorMessage = e?.response?.data?.errors?.[0]?.description

    if (errorCode === 3101) {
      errorMessage = languageData?.InvalidOtpErrorType
    } else if (errorCode === 3100) {
      errorMessage = languageData?.PhoneNumberExistsErrorType
    } else {
      errorMessage = e?.response?.data?.errors?.[0]?.description || languageData?.somethingWentWrong
    }
    yield toast(errorMessage, 'error')
  }
}

function * changePhoneNumberWorker (action) {
  try {
    const { data, setPhoneDisable } = action.payload

    yield changePhoneNumber(data)
    yield put(changePhoneNumberComplete())
    yield setPhoneDisable(true)
  } catch (e) {
    yield put(changePhoneNumberComplete())
    yield toast(e?.response?.data?.errors[0]?.description, 'error')
  }
}

function * resendPhoneOTPWorker (action) {
  const languageData = yield select(getLanguageData)
  try {
    const params = action?.payload?.params || {}

    yield resendPhoneOTP(params)
    yield put(resendPhoneOTPComplete())

    yield toast('OTP ' + languageData?.sentSuccessfully, 'success')
  } catch (e) {
    yield put(resendPhoneOTPComplete())
    yield toast(languageData?.PhoneNumberExistsErrorType || e?.response?.data?.errors[0]?.description, 'error')
  }
}

function * sendStrictPhoneOTPWorker (action) {
  try {
    const languageData = yield select(getLanguageData)
    const { phone, phoneCode, signupData, setDisableSubmit } = action.payload

    yield resendPhoneOTP({ phone, phoneCode })
    yield put(sendStrictPhoneOTPComplete())
    yield put(setStrictSignupPendingData(signupData))

    yield put(openModal({
      name: MODALS.PHONE_VERIFICATION_MODAL,
      body: <PhoneVerification isStrict signupData={signupData} />,
      modalProps: {
        className: 'phone-verification-modal-wrapper'
      }
    }))

    yield toast('OTP ' + languageData?.sentSuccessfully, 'success')

    if (setDisableSubmit) {
      yield setDisableSubmit(false)
    }
  } catch (e) {
    yield put(sendStrictPhoneOTPComplete())
    yield toast(e?.response?.data?.errors[0]?.description, 'error')

    if (action.payload?.setDisableSubmit) {
      yield action.payload.setDisableSubmit(false)
    }
  }
}
